import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  TextField,
  IconButton,
  CircularProgress,
  useMediaQuery,
  Theme,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ChatWindowWrapper } from "./ChatWindowWrapper";
import { MessageInput } from "./MessageInput";
import { MessageList } from "./MessageList";
import { Helper } from "../../tools/Helper";
import { api_Routes } from "../../api_Routes";
import pusher from "../../tools/pusher";

interface Message {
  id: number;
  body: string;
  created_at: string;
  sender: {
    id: number;
    name: string;
    avatar?: string;
  };
}

interface ChatComponentProps {
  conversationId: number;
  currentUser: {
    id: number;
    name: string;
    avatar?: string;
  };
  initialMessages: Message[];
}

interface ChatWindowProps {
  setSelectedConversation: (id: number | null) => void;
  selectedConversation: number | null;
  messages: Message[];
  currentUser: {
    id: number;
    name: string;
    avatar?: string;
  };
  loadMoreMessages: () => void;
  isLoadingMore: boolean;
  hasMore: boolean;
  messagesContainerRef: React.RefObject<HTMLUListElement>;
  maintainScrollPosition: () => void;
  setMessages: (prev: any) => void;
}

const ChatWindow = ({
  setSelectedConversation,
  messages,
  currentUser,
  selectedConversation,
  loadMoreMessages,
  isLoadingMore,
  hasMore,
  messagesContainerRef,
  maintainScrollPosition,
  setMessages,
}: ChatWindowProps) => {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    const tempMessage: Message = {
      id: Date.now(),
      body: newMessage,
      created_at: new Date().toISOString(),
      sender: currentUser,
    };
    setMessages((prev) => [tempMessage, ...prev]);
    setNewMessage("");
    setLoading(true);
    try {
      const { response, message } = await Helper.Post({
        url: `${api_Routes.UserChat.SendMessage}?conversation_id=${selectedConversation}&body=${newMessage}`,
        hasToken: true,
      });

      if (!response) throw new Error("Failed to send message");
      setNewMessage("");
    } catch (error) {
      setMessages((prev) => prev.filter((msg) => msg.id !== tempMessage.id));
      console.error("Error sending message:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (isLoadingMore || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const scrollPosition = scrollHeight - (scrollTop + clientHeight);

      if (scrollPosition > 200) return;
      loadMoreMessages();
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [isLoadingMore, hasMore, loadMoreMessages]);

  // Temporary debug - list all Pusher connection states

  useEffect(() => {
    if (!selectedConversation || !currentUser.id) {
      // console.log('Skipping subscription - missing conversation or user ID');
      return;
    }

    const channelName = `private-chat.${selectedConversation}.${currentUser.id}`;
    // console.log('Attempting to subscribe to:', channelName);

    const channel = pusher.subscribe(channelName);

    // Enhanced subscription logging
    channel.bind('pusher:subscription_succeeded', () => {
      // console.log('✅ Subscription succeeded to:', channelName);
      // console.log('Channel subscription state:', channel.subscriptionPending);
    });

    channel.bind('pusher:subscription_error', (status: any) => {
      // console.error('❌ Subscription failed:', status);
    });

    const handleNewMessage = (data: any) => {
      // console.log('📩 New message received:', data);

      const newMessage: Message = {
        id: data.id,
        body: data.body,
        created_at: data.created_at,
        sender: data.sender
      };

      // Update state while preserving existing messages
      setMessages(prev => {
        // Check if message already exists to prevent duplicates
        const exists = prev.some(msg => msg.id === newMessage.id);
        return exists ? prev : [newMessage, ...prev];
      });
      // ... rest of your message handling
    };

    channel.bind('UserChatMessage', handleNewMessage);

    // Log initial subscription state
    // console.log('Initial channel state:', {
    //   name: channel.name,
    //   subscribed: channel.subscribed,
    //   subscriptionPending: channel.subscriptionPending
    // });

    return () => {
      // console.log('Unsubscribing from:', channelName);
      channel.unbind_all();
      pusher.unsubscribe(channelName);
    };
  }, [selectedConversation, currentUser.id, setMessages]);

  useEffect(() => {
    const logConnectionState = () => {
      // console.log('Current Pusher connection state:', pusher.connection.state);
    };

    // Log initial state
    logConnectionState();

    // Set up periodic checks
    const interval = setInterval(logConnectionState, 5000);

    return () => clearInterval(interval);
  }, []);
  return (
    <ChatWindowWrapper>
      <Box
        p={2}
        sx={{
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        {isMobile && (
          <IconButton onClick={() => setSelectedConversation(null)}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography variant="h6">Chat</Typography>
      </Box>
      {isLoadingMore && (
        <CircularProgress sx={{ alignSelf: "center", my: 2 }} />
      )}
      <MessageList
        ref={messagesContainerRef}
        sx={{
          display: "flex",
          flexDirection: messages.length ? "column-reverse" : "column",
          overflowY: "auto",
          height: "calc(100vh - 160px)",
          position: "relative",
        }}
      >
        {messages.length === 0 && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              width: "100%",
              px: 2,
            }}
          >
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              Send a message to start the conversation!
            </Typography>
          </Box>
        )}

        {messages.map((message) => (
          <ListItem
            key={message.id}
            sx={{
              flexDirection:
                message.sender.id === currentUser.id ? "row-reverse" : "row",
              alignItems: "flex-start",
              gap: 2,
              px: 3,
            }}
          >
            <ListItemAvatar>
              <Avatar
                src={message.sender.avatar}
                sx={{
                  width: 40,
                  height: 40,
                  ...(message.sender.id === currentUser.id && {
                    order: 1,
                  }),
                }}
              >
                {message.sender.name[0]}
              </Avatar>
            </ListItemAvatar>

            <ListItemText
              sx={{
                maxWidth: { xs: "20%", sm: "75%", md: "15%" },
                minWidth: "80px",
                width: "fit-content",
                bgcolor:
                  message.sender.id === currentUser.id
                    ? "primary.main"
                    : "gray",
                color:
                  message.sender.id === currentUser.id
                    ? "primary.contrastText"
                    : "text.primary",
                borderRadius: 4,
                p: 2,
                ml: message.sender.id === currentUser.id ? 0 : 2,
                mr: message.sender.id === currentUser.id ? 2 : 0,
                wordBreak: "break-word",
                "& .MuiListItemText-primary": {
                  display: "inline-block",
                },
                "& .MuiListItemText-secondary": {
                  textAlign: "right",
                },
              }}
              primary={message.body}
              secondary={
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    fontSize: "0.75rem",
                    color:
                      message.sender.id === currentUser.id
                        ? "rgba(255,255,255,0.7)"
                        : "text.secondary",
                    mt: 1,
                  }}
                >
                  {new Date(message.created_at).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Box>
              }
            />
          </ListItem>
        ))}
        <div ref={messagesEndRef} />
      </MessageList>

      <MessageInput>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={(e) =>
            e.key === "Enter" && !loading && handleSendMessage()
          }
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 24,
            },
          }}
        />

        <IconButton
          color="primary"
          onClick={handleSendMessage}
          disabled={loading || !newMessage.trim()}
          sx={{
            bgcolor: "primary.main",
            color: "white",
            "&:hover": {
              bgcolor: "primary.dark",
            },
            ...(isMobile && {
              minWidth: "40px",
              height: "40px",
            }),
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <SendIcon fontSize="small" />
          )}
        </IconButton>
      </MessageInput>
    </ChatWindowWrapper>
  );
};

export default ChatWindow;
